import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ShepSearchBar,
  ShepHorizontalMenu,
  ShepButton,
} from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import PopUpDataGrid from "@/components/DataGrid/PopUpDataGrid";
import {
  GridActionsCellItem,
  GridToolbar,
  GridMenu,
} from "@mui/x-data-grid-pro";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useDispatch } from "react-redux";
import { UserRoles } from "@/utils/utils";

const AddUserDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);

  const handleEmailChange = (e) => {
    const inputEmail = e.target.value;
    setEmail(inputEmail);
  };

  const [role, setRole] = useState("member");
  const handleChange = (event) => {
    setRole(event.target.value);
  };

  useEffect(() => {
    console.log(role);
  }, [role]);

  const handleAddUser = (userNameToAdd) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmailRegex = emailRegex.test(email);
    setIsEmailValid(isEmailRegex);

    if (isEmailRegex) {
      axios
        .post(`/account/users`, {
          UserName: userNameToAdd,
          Role: role,
        })
        .then((response) => {
          if (response.data == null) {
            return;
          }
          dispatch(
            createAlert({
              status: "Success",
              alertType: "result",
              message: response.data,
              buttonLabel: "Close",
            }),
          );
          setIsEmailValid(true);
          setEmail("");
          setRole("member");
          onClose();
        })
        .catch((err) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: err.response.data,
              buttonLabel: "Close",
            }),
          );
          setIsEmailValid(true);
          setEmail("");
          onClose();
        });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        variant="h4"
        className="title-header"
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
        }}
      >
        Create A New User
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Please use a valid email address
        </Typography>
        <TextField
          hiddenLabel
          variant="outlined"
          fullWidth
          margin="normal"
          value={email}
          onChange={handleEmailChange}
          error={!isEmailValid}
          helperText={!isEmailValid ? "Invalid email address" : ""}
        />
        <Box sx={{ minWidth: 120 }}>
          <FormControl fullWidth>
            <InputLabel id="demo-simple-select-label">role</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={role}
              label="role"
              onChange={handleChange}
            >
              <MenuItem value={"member"}>member</MenuItem>
              <MenuItem value={"admin"}>admin</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <ShepButton onClick={() => handleAddUser(email)} variant="primary">
          Add User
        </ShepButton>
        <ShepButton onClick={onClose} variant="secondary">
          Cancel
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default function ManageUsers({ open, onClose, user }) {
  dayjs.extend(utc);

  const [rows, setRows] = useState([]);
  const [maximumUsers, setMaximumUsers] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const dispatch = useDispatch();

  const actions = [
    { label: "Add User", onClick: () => handleAddUserOpen(true) },
  ];

  const fetchRows = useCallback(() => {
    setIsLoading(true);
    axios
      .get(`/account/users`)
      .then((response) => {
        if (response.data == null) {
          return;
        }

        const manageUserData = response.data;
        const rowsWithIds = manageUserData.UserGridDatas.map((row) => ({
          ...row,
          id: row.LeapFrogBetaUserId,
        }));

        setMaximumUsers(manageUserData.MaximumUsers);
        setRows(rowsWithIds);
      })
      .catch((err) => console.log(err));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
  }, [user, fetchRows]);

  const handleOnChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddUserOpen = (isDialogOpen) => {
    setIsAddUserDialogOpen(isDialogOpen);

    if (isAddUserDialogOpen === false) {
      fetchRows();
    }
  };

  const handleDeleteUser = useCallback(
    (userName) => {
      axios
        .post("/account/users/delete", userName)
        .then((response) => {
          if (response.data != null) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "single",
                message: response.data,
              }),
            );

            fetchRows();
          }
        })
        .catch((err) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "single",
              message: err.response.data,
            }),
          );
        });
    },
    [fetchRows],
  );

  const handleEnableUser = useCallback(
    (userName, shouldEnableUser) => {
      axios
        .put("/account/users/status", {
          UserName: userName,
          IsEnabled: shouldEnableUser,
        })
        .then((response) => {
          if (response.data != null) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                message: response.data,
              }),
            );

            if (response.data !== "Unable to update user account.") {
              fetchRows();
            }
          }
        })
        .catch((err) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: err.response.data,
            }),
          );
        });
    },
    [fetchRows],
  );

  const handleRoleChange = useCallback(
    (userName, newRole) => {
      axios
        .put("/account/users/role", {
          UserName: userName,
          Role: newRole,
        })
        .then((response) => {
          if (response.data != null) {
            dispatch(
              createAlert({
                status: "Success",
                alertType: "result",
                message: response.data,
              }),
            );
            fetchRows();
          }
        })
        .catch((err) => {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: err.response.data,
            }),
          );
        });
    },
    [fetchRows],
  );

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const values = Object.values(row);
        const disabledText = row["isDisabled"] ? "disabled" : "active";

        return values.some((value) => {
          if (disabledText.includes(searchTerm.toLowerCase())) {
            return true;
          }
          return value
            ? value.toString().toLowerCase().includes(searchTerm.toLowerCase())
            : false;
        });
      }),
    [rows, searchTerm],
  );

  const enabledUsers = useMemo(
    () => rows.filter((row) => !row.isDisabled).length,
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "UserName",
        headerName: "User Name",
        width: 250,
      },
      {
        field: "IsDisabled",
        headerName: "Account Status",
        width: 200,
        renderCell: (params) => {
          return params.value ? "Disabled" : "Active";
        },
      },
      {
        field: "RoleName",
        headerName: "Role",
        width: 120,
      },
      {
        field: "LastSuccessIPAddress",
        headerName: "Last IP Address",
        width: 160,
        flex: 1,
      },
      {
        field: "LastSuccessLoginDate",
        headerName: "Last Login Date",
        width: 160,
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) {
            return "";
          }
          return dayjs(params.value)
            .utc(true)
            .local()
            .format("YYYY.MM.DD HH:mm");
        },
      },
      {
        field: "Actions",
        type: "actions",
        headerName: "",
        width: 60,
        getActions: (params) => {
          const rowData = params.row;

          if (user.userName === rowData.UserName) {
            return [];
          }

          const gridActionsList = [
            <GridActionsCellItem
              label="Delete"
              onClick={() => handleDeleteUser(rowData.UserName)}
              color="inherit"
              showInMenu
            />,
          ];

          if (rowData.IsDisabled) {
            gridActionsList.push(
              <GridActionsCellItem
                label="Enable"
                onClick={() => handleEnableUser(rowData.UserName, false)}
                color="inherit"
                showInMenu
              />,
            );
          }

          if (rowData.UserName !== user.userName) {
            if (rowData.RoleName === "admin") {
              gridActionsList.push(
                <GridActionsCellItem
                  label="Change Role to Member"
                  onClick={() => handleRoleChange(rowData.UserName, "member")}
                  color="inherit"
                  showInMenu
                />,
              );
            } else if (rowData.RowName !== UserRoles.SuperAdmin) {
              gridActionsList.push(
                <GridActionsCellItem
                  label="Change Role to Admin"
                  onClick={() => handleRoleChange(rowData.UserName, "admin")}
                  color="inherit"
                  showInMenu
                />,
              );
            }
          }

          return gridActionsList;
        },
      },
    ],
    [user.userName, handleDeleteUser, handleEnableUser, handleRoleChange],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      className="manage-user-dialog"
    >
      <DialogTitle
        variant="h4"
        className="title-header"
        sx={{
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
        }}
      >
        Manage Users
      </DialogTitle>

      <DialogContent style={{ padding: "0px 24px" }}>
        <Typography variant="body1">
          {`${maximumUsers !== null ? `Maximum Users: ${maximumUsers}` : ""}
            ${maximumUsers !== null && enabledUsers !== null ? ", " : ""}
            ${enabledUsers !== null ? `Enabled Users: ${enabledUsers}` : ""}`}
        </Typography>
        <div style={{ display: "flex", margin: "15px 0px" }}>
          <ShepSearchBar
            hiddenLabel
            type="search"
            value={searchTerm}
            onChange={handleOnChange}
            style={{ marginRight: "30%", flex: 1 }}
          />
          <ShepHorizontalMenu actions={actions} />
        </div>

        <PopUpDataGrid
          rows={filteredRows}
          columns={columns}
          whiteHeader={true}
          checkboxSelection={true}
          components={{
            Toolbar: GridToolbar,
            ActionsCell: GridActionsCellItem,
            Menu: GridMenu,
          }}
          autoHeight
          style={{ width: "100%" }}
          loading={isLoading}
        />
      </DialogContent>
      <DialogActions style={{ marginTop: "15px" }}>
        <ShepButton variant="primary" onClick={onClose}>
          Close
        </ShepButton>
      </DialogActions>
      <AddUserDialog
        open={isAddUserDialogOpen}
        onClose={() => {
          setIsAddUserDialogOpen(false);
          fetchRows();
        }}
      />
    </Dialog>
  );
}
